'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

import { toast } from 'sonner';

interface CartItem {
  id: string;
  name: string;
  price: number;
  qty: number;
  image: string;
  alt: string;
}

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
  items: CartItem[];
  setItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
  total: number;
}

export default function CartDrawer({ open, onClose, items, setItems, total }: CartDrawerProps) {
  const updateQty = (id: string, delta: number) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    toast.success('Item removed from cart');
  };

  const handleCheckout = () => {
    toast.success('Proceeding to checkout', { description: 'Order attributed to Maria Santos\'s store.' });
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-accent/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-card border-l border-border z-50 flex flex-col shadow-elevated transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-foreground">Shopping Cart</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Maria Santos's Store</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors" aria-label="Close cart">
            <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="ShoppingBagIcon" size={40} className="text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-sm font-medium text-muted-foreground mb-1">Your cart is empty</p>
              <p className="text-xs text-muted-foreground">Browse Maria's featured products to get started</p>
            </div>
          ) : (
            items.map(item => (
              <div key={`cart-item-${item.id}`} className="flex gap-3">
                <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                  <AppImage src={item.image} alt={item.alt} fill sizes="64px" className="object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground leading-snug mb-1">{item.name}</p>
                  <p className="text-sm font-bold text-foreground tabular-nums">${item.price}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center border border-border rounded-lg overflow-hidden">
                      <button onClick={() => updateQty(item.id, -1)} className="w-7 h-7 flex items-center justify-center hover:bg-muted transition-colors" aria-label="Decrease quantity">
                        <Icon name="MinusIcon" size={12} className="text-muted-foreground" />
                      </button>
                      <span className="w-8 text-center text-xs font-semibold tabular-nums">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, 1)} className="w-7 h-7 flex items-center justify-center hover:bg-muted transition-colors" aria-label="Increase quantity">
                        <Icon name="PlusIcon" size={12} className="text-muted-foreground" />
                      </button>
                    </div>
                    <button onClick={() => removeItem(item.id)} className="text-danger hover:text-danger/80 transition-colors ml-auto" aria-label="Remove item">
                      <Icon name="TrashIcon" size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-border p-5 space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-semibold tabular-nums">${total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-success font-semibold">Calculated at checkout</span>
              </div>
              <div className="flex justify-between text-base font-bold border-t border-border pt-2">
                <span>Total</span>
                <span className="tabular-nums">${total.toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={handleCheckout}
              className="btn-primary w-full py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            >
              Proceed to Checkout
              <Icon name="ArrowRightIcon" size={16} />
            </button>
            <p className="text-center text-[10px] text-muted-foreground">
              Purchase attributed to Maria Santos • Secure checkout
            </p>
          </div>
        )}
      </div>
    </>
  );
}